/**
 * HelpPanel — the persistent surface for an active flow.
 *
 * This replaces the transient step bubble. The panel is the guide: every step is
 * listed with its state at all times, so the user always knows where they are and
 * what's left, and Exit is always in reach in the header rather than hidden on a
 * separate pill.
 *
 * Two distinct notions of "step", kept apart on purpose:
 *   active   — what the engine is watching for completion
 *   selected — what the user is currently reading
 * Clicking an upcoming step previews it; it does not move the engine. Jumping the
 * engine would silently break the detector chain and let a flow finish out of
 * order.
 *
 * "Show me" is offered only on the active step: pointing at a control that isn't
 * reachable yet would just report "can't find it", which teaches nothing.
 */

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const STATE_ICON = { done: '✓', active: '▸', todo: '○' };

/**
 * Stop `mousedown` from escaping the guide.
 *
 * The panel is portalled to document.body, so to every "click outside to close"
 * handler in the app a click on the guide looks like a click on the page. There
 * are 48 such listeners across the codebase and every one of them is
 * `document.addEventListener('mousedown', …)` in bubble phase — so halting
 * mousedown at our own root means none of them ever sees it.
 *
 * Concretely: clicking "Show me" for the move-out flow's second step used to
 * close the kebab menu the step is pointing at, making the highlight impossible.
 *
 * Only `mousedown` is stopped. `click` is deliberately left alone: React's
 * synthetic onClick handlers and the guide's own spotlight-dismiss listener both
 * depend on it.
 */
function useSwallowMouseDown(rebindKey) {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const stop = (e) => e.stopPropagation();
        el.addEventListener('mousedown', stop);
        return () => el.removeEventListener('mousedown', stop);
    }, [rebindKey]);
    return ref;
}

const HelpPanel = ({
    flow,
    steps,
    activeIndex,
    selectedIndex,
    finished,
    collapsed,
    spotlightOn,
    anchorStatus,
    timedOut,
    offRoute,
    isManualStep,
    dockStyle,
    onSelectStep,
    onToggleSpotlight,
    onMarkDone,
    onReturn,
    onCollapse,
    onExpand,
    onExit,
    onRunAnother,
}) => {
    /* Bound before the early return so hook order stays stable; rebinds when the
       root element swaps between pill and panel. */
    const rootRef = useSwallowMouseDown(collapsed);

    /* Collapsed: a pill that says where you are and gets you back. */
    if (collapsed) {
        return createPortal(
            <div className="phg-pill" ref={rootRef} style={dockStyle || undefined}>
                <button type="button" className="phg-pill__btn" onClick={onExpand}>
                    <span className="phg-pill__label">
                        {finished
                            ? flow.title
                            : `${flow.title} — step ${Math.min(activeIndex + 1, steps.length)} of ${steps.length}`}
                    </span>
                </button>
                <button type="button" className="phg-iconbtn" onClick={onExit} aria-label="Exit guide">&#x2715;</button>
            </div>,
            document.body,
        );
    }

    const selected = steps[selectedIndex];
    const isActiveSelected = selectedIndex === activeIndex;

    return createPortal(
        <div className="phg-panel" ref={rootRef} style={dockStyle || undefined} role="dialog" aria-label={`${flow.title} guide`}>
            <div className="phg-panel__head">
                <span className="phg-panel__title">{flow.title}</span>
                <span className="phg-panel__headbtns">
                    <button type="button" className="phg-iconbtn" onClick={onCollapse} aria-label="Collapse guide" title="Collapse">&#x2013;</button>
                    <button type="button" className="phg-iconbtn" onClick={onExit} aria-label="Exit guide" title="Exit guide">&#x2715;</button>
                </span>
            </div>

            {finished ? (
                <div className="phg-panel__done">
                    <div className="phg-panel__doneTitle">{flow.success?.title || 'All set'}</div>
                    <div className="phg-panel__doneBody">{flow.success?.body}</div>
                    <div className="phg-panel__actions">
                        <button type="button" className="phg-btn phg-btn--ghost" onClick={onRunAnother}>Run another</button>
                        <button type="button" className="phg-btn phg-btn--primary" onClick={onExit}>Close</button>
                    </div>
                </div>
            ) : (
                <>
                    <ol className="phg-steps">
                        {steps.map((s, i) => {
                            const state = i < activeIndex ? 'done' : i === activeIndex ? 'active' : 'todo';
                            const isSelected = i === selectedIndex;
                            return (
                                <li key={s.id} className={`phg-step phg-step--${state}${isSelected ? ' is-selected' : ''}`}>
                                    <button
                                        type="button"
                                        className="phg-step__row"
                                        onClick={() => onSelectStep(i)}
                                        aria-current={state === 'active' ? 'step' : undefined}
                                        aria-expanded={isSelected}
                                    >
                                        <span className="phg-step__icon" aria-hidden="true">{STATE_ICON[state]}</span>
                                        <span className="phg-step__title">{s.title}</span>
                                    </button>

                                    {isSelected ? (
                                        <div className="phg-step__detail">
                                            <div className="phg-step__body">{s.body}</div>

                                            {offRoute && isActiveSelected ? (
                                                <div className="phg-note phg-note--warn">
                                                    You&rsquo;ve moved to a different page. This step happens back on the previous one.
                                                    <button type="button" className="phg-btn phg-btn--primary phg-btn--sm phg-note__btn" onClick={onReturn}>
                                                        Take me back
                                                    </button>
                                                </div>
                                            ) : null}

                                            {spotlightOn && anchorStatus === 'missing' && isActiveSelected && !offRoute ? (
                                                <div className="phg-note phg-note--warn">
                                                    Can&rsquo;t find it on this screen &mdash; your role or campus setup may not include it.
                                                </div>
                                            ) : null}

                                            {timedOut && isActiveSelected && !offRoute ? (
                                                <div className="phg-note">
                                                    Still waiting. If you&rsquo;ve already done this, mark it done.
                                                </div>
                                            ) : null}

                                            {isActiveSelected ? (
                                                <div className="phg-step__actions">
                                                    {s.anchor ? (
                                                        <button
                                                            type="button"
                                                            /* Violet while the ring is up, so the control and
                                                               the mark it drew match; quiet ghost otherwise. */
                                                            className={`phg-btn phg-btn--sm ${spotlightOn ? 'phg-btn--highlight' : 'phg-btn--ghost'}`}
                                                            onClick={onToggleSpotlight}
                                                        >
                                                            {spotlightOn ? 'Hide' : 'Show me'}
                                                        </button>
                                                    ) : null}
                                                    <span className="phg-step__spacer" />
                                                    {isManualStep ? (
                                                        /* Nothing observable distinguishes "filled the form in"
                                                           from "still typing", so this step can only be
                                                           completed by the user saying so. It needs an
                                                           affirmative primary action — "Already done" alone
                                                           reads as a skip and leaves the user stuck. */
                                                        <button type="button" className="phg-btn phg-btn--primary phg-btn--sm" onClick={onMarkDone}>
                                                            Done &mdash; next step
                                                        </button>
                                                    ) : (
                                                        /* Auto-detected steps advance themselves, so this is
                                                           only an escape hatch — kept quiet on purpose. */
                                                        <button type="button" className="phg-link" onClick={onMarkDone}>
                                                            Already done
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="phg-step__hint">
                                                    {selectedIndex > activeIndex ? 'Coming up' : 'Completed'}
                                                </div>
                                            )}
                                        </div>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ol>

                    <div className="phg-panel__foot">
                        <span className="phg-panel__progress">
                            {Math.min(activeIndex, steps.length)} of {steps.length} done
                        </span>
                        {/* Switching tasks mid-flow is safe and needs no warning: the
                            guide never performs actions itself, so whatever the user
                            already did in the app stays done. Only the guide resets.
                            Reuses the same handler as the success screen's "Run
                            another" — exit, then reopen the picker. */}
                        <button type="button" className="phg-link" onClick={onRunAnother}>
                            New task
                        </button>
                    </div>
                </>
            )}
        </div>,
        document.body,
    );
};

export default HelpPanel;
